const TVA = parseFloat(process.env.REACT_APP_TVA);

/**
 * Mirrors the backend's getDefaultUnitPriceTtc (CartServiceImpl): finds the highest
 * quantity-tier discount whose threshold the given quantity meets or exceeds.
 * @param {Object} product - The product object, with an optional `discounts` array of {quantity, price} (TTC)
 * @param {number} quantity - The quantity being priced
 * @returns {number|null} - The tier unit price (TTC), or null if no tier applies
 */
const getQuantityTierPriceTTC = (product, quantity) => {
  const discounts = product?.discounts;
  if (!discounts || discounts.length === 0 || !quantity) return null;
  const matching = [...discounts]
    .filter((d) => quantity >= d.quantity)
    .sort((a, b) => b.quantity - a.quantity)[0];
  return matching ? matching.price : null;
};

/**
 * Get the appropriate price (HT or TTC) based on user concession status and, for the
 * non-concession default price, any quantity-tier discount — mirrors the backend's
 * applyPricingForCartDetail so the live order/quote preview matches what gets saved.
 * @param {Object} product - The product object with priceHT, priceTTC, priceConcessionHT, priceConcessionTTC, discounts
 * @param {Object} user - The user object with concession property
 * @param {string} type - 'HT' or 'TTC'
 * @param {number} quantity - The quantity being priced (for tier discounts); defaults to 1 (no tier)
 * @returns {number|null} - The appropriate price or null if not available
 */
export const getPrice = (product, user, type = 'HT', quantity = 1) => {
  if (!product) return null;

  const isConcession = user?.concession === true;
  const priceType = type === 'HT' ? 'HT' : 'TTC';

  if (isConcession) {
    const concessionPrice = product[`priceConcession${priceType}`];
    // If concession price exists and is not null, use it
    if (concessionPrice !== null && concessionPrice !== undefined) {
      return concessionPrice;
    }
  } else {
    // Quantity-based pricing only applies to the default (non-concession) price.
    const tierPriceTTC = getQuantityTierPriceTTC(product, quantity);
    if (tierPriceTTC != null) {
      return type === 'HT' ? tierPriceTTC / (1 + TVA) : tierPriceTTC;
    }
  }

  // Fallback to normal price
  return product[`price${priceType}`] || null;
};

/**
 * Get price HT based on user concession status and quantity-tier discounts
 * @param {Object} product - The product object
 * @param {Object} user - The user object with concession property
 * @param {number} quantity - The quantity being priced (for tier discounts)
 * @returns {number|null} - The appropriate price HT or null
 */
export const getPriceHT = (product, user, quantity = 1) => {
  return getPrice(product, user, 'HT', quantity);
};

/**
 * Get price TTC based on user concession status and quantity-tier discounts
 * @param {Object} product - The product object
 * @param {Object} user - The user object with concession property
 * @param {number} quantity - The quantity being priced (for tier discounts)
 * @returns {number|null} - The appropriate price TTC or null
 */
export const getPriceTTC = (product, user, quantity = 1) => {
  return getPrice(product, user, 'TTC', quantity);
};

/**
 * Full client-side mirror of the backend's applyPricingForCartDetail (CartServiceImpl) —
 * used for the live order/quote drafting forms, where there is no saved CartDetail yet
 * to read the already-discounted price back from (unlike ProductRow, which trusts the
 * server's unitPrice/priceHT/priceTTC directly for already-persisted lines).
 *
 * Applies, in the same order as the backend: quantity-tier or concession pricing first,
 * then the product's own flat discount (only when neither concession nor a tier price
 * applied), then the per-line sales discount.
 *
 * @param {Object} product - The product object (priceHT/TTC, priceConcessionHT/TTC, discounts, discount)
 * @param {Object} user - The user object with concession property
 * @param {number} quantity - The quantity being priced
 * @param {number} salesDiscount - The per-line "remise spéciale" percentage
 * @returns {{referencePriceHT: number, referencePriceTTC: number, discountedPriceHT: number, discountedPriceTTC: number, hasDiscount: boolean}|null}
 */
export const getEffectiveLinePrice = (product, user, quantity, salesDiscount = 0) => {
  if (!product) return null;

  const isConcession = user?.concession === true;
  const usedQuantityPricing = !isConcession && getQuantityTierPriceTTC(product, quantity) != null;

  const referencePriceHT = getPriceHT(product, user, quantity) ?? 0;
  const referencePriceTTC = getPriceTTC(product, user, quantity) ?? 0;

  let discountedPriceHT = referencePriceHT;
  let discountedPriceTTC = referencePriceTTC;

  // The product's own flat discount only applies to the default (non-concession,
  // non-tier-priced) price — mirrors applyPricingForCartDetail exactly.
  if (!isConcession && !usedQuantityPricing && product.discount) {
    discountedPriceHT = discountedPriceHT * (1 - product.discount / 100);
    discountedPriceTTC = discountedPriceTTC * (1 - product.discount / 100);
  }

  if (salesDiscount) {
    discountedPriceHT = discountedPriceHT * (1 - salesDiscount / 100);
    discountedPriceTTC = discountedPriceTTC * (1 - salesDiscount / 100);
  }

  return {
    referencePriceHT,
    referencePriceTTC,
    discountedPriceHT,
    discountedPriceTTC,
    hasDiscount: discountedPriceTTC < referencePriceTTC - 0.001,
  };
};
